import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';
import './index.css';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!, // This will be provided by the RouterProvider context prop
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useContext, Component, type ReactNode } from 'react';

// Error boundary so that Google OAuth / reCAPTCHA SDK failures on HTTP
// don't crash the entire app — sign-in/sign-up pages still render,
// social login buttons just won't function.
class SilentErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.children : this.props.children;
  }
}

// Polyfill `crypto.randomUUID` for non-secure (HTTP) contexts and older browsers.
// The inline script in index.html already does this for third-party SDKs;
// this copy ensures it's available for any module-level calls too.
try {
  const _crypto = globalThis.crypto as Crypto & { randomUUID?: () => string };
  if (_crypto && typeof _crypto.randomUUID !== 'function') {
    const getRandValues = _crypto.getRandomValues
      ? (buf: Uint8Array) => _crypto.getRandomValues(buf)
      : (buf: Uint8Array) => {
          for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
          return buf;
        };

    (_crypto as Crypto & { randomUUID: () => string }).randomUUID = () => {
      const bytes = new Uint8Array(16);
      getRandValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
    };
  }
} catch {
  /* ignore */
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const RECAPTCHA_SITE_KEY: string = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '';

function App() {
  const auth = useContext(AuthContext);

  if (!auth) {
    return null; // Or a loading spinner
  }

  return (
    <RouterProvider
      router={router}
      context={{ auth: { isAuthenticated: auth.isAuthenticated, user: auth.user } }}
    />
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <SilentErrorBoundary>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
          <QueryClientProvider client={queryClient}>
            <SilentErrorBoundary>
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <AuthProvider>
                  <App />
                  <Toaster
                    position="bottom-right"
                    toastOptions={{
                      duration: 3500,
                      style: {
                        background: 'rgba(17, 17, 17, 0.9)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        color: '#fff',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        maxWidth: '400px',
                        padding: '12px 20px',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                      },
                      success: {
                        iconTheme: {
                          primary: '#3B82F6',
                          secondary: '#fff',
                        },
                      },
                      error: {
                        iconTheme: {
                          primary: '#EF4444',
                          secondary: '#fff',
                        },
                      },
                    }}
                  />
                </AuthProvider>
              </GoogleOAuthProvider>
            </SilentErrorBoundary>
          </QueryClientProvider>
        </GoogleReCaptchaProvider>
      </SilentErrorBoundary>
    </HelmetProvider>
  </StrictMode>,
);